import type { Order } from "../types/api";
import { useState } from "react";
import { AdjustmentsHorizontalIcon, FunnelIcon } from "@heroicons/react/24/outline";

// Add type definition for custom Clarity attribute
declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    'data-clarity-mask'?: string;
  }
}

interface ExcludedOrders {
  wrongWebsite?: number;
  nonEuroCurrency?: number;
  usedProducts?: number;
  cancelledOrders?: number;
  outsideDateRange?: number;
  namesMismatch?: number;
  missingData?: number;
  blacklistedProducts?: number;
}

interface Identity {
  name: string;
  gmv: number;
  eligibleGMV?: number;
  totalReturns?: number;
  returnCount?: number;
  orderCount: number;
  eligibleOrderCount: number;
  selected: boolean;
  isCompany: boolean;
  excludedOrders?: ExcludedOrders;
}

interface OrdersTableProps {
  orders: Order[];
  rejectionStats?: ExcludedOrders;
  identities?: Identity[];
}

const ALL_COLUMNS = [
  { key: "orderId", label: "Order ID", defaultVisible: true },
  { key: "orderDate", label: "Date", defaultVisible: true },
  { key: "amount", label: "Amount", defaultVisible: true },
  { key: "returnAmount", label: "Return", defaultVisible: true },
  { key: "asin", label: "ASIN", defaultVisible: true },
  { key: "productName", label: "Product", defaultVisible: false },
  { key: "quantity", label: "Qty", defaultVisible: false },
  { key: "unitPrice", label: "Unit Price", defaultVisible: false },
  { key: "shipmentItemSubtotal", label: "Subtotal", defaultVisible: false },
  { key: "productCondition", label: "Condition", defaultVisible: false },
  { key: "orderStatus", label: "Status", defaultVisible: false },
  { key: "shipmentStatus", label: "Ship Status", defaultVisible: false },
  { key: "website", label: "Website", defaultVisible: false },
  { key: "isEligible", label: "Eligible", defaultVisible: false },
  { key: "inEvidencePeriod", label: "In Period", defaultVisible: false },
  { key: "isPhysicalProduct", label: "Physical", defaultVisible: false },
];

export default function OrdersTable({ orders, rejectionStats, identities }: OrdersTableProps) {
  const [showZeroEurOrders, setShowZeroEurOrders] = useState(false);
  const [showOutsideDateRange, setShowOutsideDateRange] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showIdentityDetails, setShowIdentityDetails] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: col.defaultVisible }), {})
  );

  const [filters, setFilters] = useState<Record<string, string>>(
    ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: "" }), {})
  );

  // Filter orders
  const filteredOrders = orders.filter((order: Order) => {
    if (!showZeroEurOrders && order.amount === 0) return false;
    if (!showOutsideDateRange && order.inEvidencePeriod === false) return false;

    for (const col of ALL_COLUMNS) {
      const filterValue = filters[col.key]?.toLowerCase();
      if (!filterValue) continue;

      const orderValue = String(order[col.key] || "").toLowerCase();
      if (!orderValue.includes(filterValue)) return false;
    }

    return true;
  });

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeColumns = ALL_COLUMNS.filter(col => visibleColumns[col.key]);

  return (
    <div className="space-y-4">
      {/* Aggregated Rejection Statistics */}
      {rejectionStats && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-2">Filtered Orders (All Identities)</h4>
          <div className="grid grid-cols-2 md:grid-cols-9 gap-3 text-xs text-yellow-800 dark:text-yellow-300">
            <div><span className="font-medium">Orders:</span> {identities?.reduce((sum, i) => sum + (i.orderCount || 0), 0) || orders.length}</div>
            <div><span className="font-medium">Wrong Website:</span> {rejectionStats.wrongWebsite || 0}</div>
            <div><span className="font-medium">Non-EUR:</span> {rejectionStats.nonEuroCurrency || 0}</div>
            <div><span className="font-medium">Used Products:</span> {rejectionStats.usedProducts || 0}</div>
            <div><span className="font-medium">Cancelled:</span> {rejectionStats.cancelledOrders || 0}</div>
            <div><span className="font-medium">Blacklisted:</span> {rejectionStats.blacklistedProducts || 0}</div>
            <div><span className="font-medium">Date:</span> {rejectionStats.outsideDateRange || 0}</div>
            <div><span className="font-medium">Name Mismatch:</span> {rejectionStats.namesMismatch || 0}</div>
            <div><span className="font-medium">Returns:</span> {identities?.reduce((sum, i) => sum + (i.returnCount || 0), 0) || 0}</div>
          </div>
        </div>
      )}

      {/* Per-Identity Rejection Statistics (Collapsible) */}
      {identities && identities.length > 0 && identities.some(i => i.excludedOrders) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <button
            onClick={() => setShowIdentityDetails(!showIdentityDetails)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
              Filtered Orders by Identity ({identities.length} identities)
            </h4>
            <svg
              className={`w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform ${showIdentityDetails ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showIdentityDetails && (
            <div className="px-4 pb-4 space-y-3">
              {identities.filter(i => i.excludedOrders).map((identity) => (
                <div key={identity.name} className="border-b border-blue-200 dark:border-blue-700 pb-2 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-blue-900 dark:text-blue-100">{identity.name}</span>
                    {identity.isCompany && <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">Company</span>}
                    {identity.selected && <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">Selected</span>}
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      (€{identity.eligibleGMV?.toFixed(2) || '0.00'} Eligible{identity.totalReturns ? ` - €${identity.totalReturns.toFixed(2)} Returns` : ''} = €{identity.gmv.toFixed(2)} GMV)
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-9 gap-2 text-xs text-blue-700 dark:text-blue-300">
                    <div>Orders: {identity.orderCount || 0}</div>
                    <div>Website: {identity.excludedOrders?.wrongWebsite || 0}</div>
                    <div>Non-EUR: {identity.excludedOrders?.nonEuroCurrency || 0}</div>
                    <div>Used: {identity.excludedOrders?.usedProducts || 0}</div>
                    <div>Cancelled: {identity.excludedOrders?.cancelledOrders || 0}</div>
                    <div>Blacklisted: {identity.excludedOrders?.blacklistedProducts || 0}</div>
                    <div>Date: {identity.excludedOrders?.outsideDateRange || 0}</div>
                    <div>Missing: {identity.excludedOrders?.missingData || 0}</div>
                    <div>Returns: {identity.returnCount || 0}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {filteredOrders.length} of {orders.length} orders shown
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showZeroEurOrders}
              onChange={(e) => setShowZeroEurOrders(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Show €0 orders</span>
          </label>
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showOutsideDateRange}
              onChange={(e) => setShowOutsideDateRange(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Show outside date range</span>
          </label>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <FunnelIcon className="h-4 w-4 mr-1.5" />
            Filters
          </button>
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 mr-1.5" />
            Columns
          </button>
        </div>
      </div>

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Visible Columns</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {ALL_COLUMNS.map((col) => (
              <label key={col.key} className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={visibleColumns[col.key]}
                  onChange={() => toggleColumn(col.key)}
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-gray-700 dark:text-gray-300">{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {activeColumns.map((col) => (
                <th key={col.key} className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                  <div className="space-y-1">
                    <div>{col.label}</div>
                    {showFilters && (
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={filters[col.key]}
                        onChange={(e) => setFilters({ ...filters, [col.key]: e.target.value })}
                        className="w-full text-xs font-normal rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800" data-clarity-mask="True">
            {filteredOrders.map((order: Order) => (
              <tr key={order.orderId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                {activeColumns.map((col) => {
                  let value = order[col.key];

                  // Format specific fields
                  if (col.key === "orderDate" && value) {
                    value = new Date(value).toLocaleDateString();
                  } else if (col.key === "amount") {
                    value = `${value} ${order.currency}`;
                  } else if (col.key === "returnAmount") {
                    // Show return amount with currency, or dash if no return
                    if (value && value > 0) {
                      return (
                        <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-red-600 dark:text-red-400 font-medium">
                          -{value.toFixed(2)} {order.currency || 'EUR'}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-gray-400 dark:text-gray-500">
                        -
                      </td>
                    );
                  } else if (col.key === "isEligible" || col.key === "inEvidencePeriod" || col.key === "isPhysicalProduct") {
                    value = value ? "✓" : "✗";
                  } else if (col.key === "productName") {
                    return (
                      <td key={col.key} className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={value}>
                        {value}
                      </td>
                    );
                  }

                  return (
                    <td key={col.key} className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {value ?? "-"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
          No orders match the current filters
        </div>
      )}
    </div>
  );
}
